import {QuestionAnswerEnum, QuestionReportAnswer} from '../../../../incase-domain/analytics';
import {ELEMENT, interactiveElementTypes} from '../../../../incase-domain/elements/base/ElementTypes';
import {IQuestionElements} from '../../../../incase-domain/elements/question';
import {IElementQuestionMatchPair} from '../../../../incase-domain/elements/question/questionMatchPair';
import {IElementQuestionAnswer} from '../../../../incase-domain/elements/base/questionElementBase';
import {IShape} from '../../../../incase-domain/elements/question/imageMarkup';
import {IPresentation} from '../../../../incase-domain/presentation';
import {IElement} from '../../../../incase-domain/elements';

const getPercentage = (noOfAnswers: number, ordinal: number): number =>
    Math.ceil(((ordinal + 1) * 100) / ((noOfAnswers * (noOfAnswers + 1)) / 2));

const getMatchPairAnswer = (question: IElementQuestionMatchPair, index: number): string => {
    const left = question.leftElement.find((el) => el.id === question.pairs[index].leftElementId);
    const right = question.rightElement.find((el) => el.id === question.pairs[index].rightElementId);
    return `${left?.text} - ${right?.text}`;
};

const areAllAnswersDebatable = (question: IQuestionElements): boolean =>
    'answers' in question &&
    Array.isArray(question.answers) &&
    question.answers.length > 0 &&
    question.answers.every((answer) => answer.type === QuestionAnswerEnum.DEBATABLE);

const addAllCorrectAnswerIfNeeded = (answers: QuestionReportAnswer[], question: IQuestionElements): QuestionReportAnswer[] => {
    const allCorrectAnswer = {
        answerText: 'All correct',
        noOfAnswers: 0,
        type: QuestionAnswerEnum.CORRECT as const,
        percentage: 0,
    };
    const needsAllCorrectAnswer =
        !question.isDebatable &&
        !areAllAnswersDebatable(question) &&
        ((question.type !== ELEMENT.YES_NO_QUESTION &&
            [ELEMENT.QUESTION, ELEMENT.MULTI_SELECT, ELEMENT.PHOTO_ANSWER_QUESTION].includes(question.type) &&
            !question.singleCorrect) ||
            [ELEMENT.MATCH_PAIR_QUESTION, ELEMENT.SEQUENCE_QUESTION].includes(question.type) ||
            (ELEMENT.IMAGE_MARKUP === question.type && question.markedAreas.length > 1));
    return needsAllCorrectAnswer ? [allCorrectAnswer, ...answers] : answers;
};

export const getAnswers = (question: IQuestionElements): QuestionReportAnswer[] => {
    let reportAnswers: QuestionReportAnswer[];
    switch (question.type) {
        case ELEMENT.YES_NO_QUESTION:
            reportAnswers = [
                {
                    answerText: 'No',
                    noOfAnswers: 140,
                    type: question.correct === 0 ? QuestionAnswerEnum.CORRECT : QuestionAnswerEnum.INCORRECT,
                    percentage: 70,
                },
                {
                    answerText: 'Yes',
                    noOfAnswers: 60,
                    type: question.correct === 1 ? QuestionAnswerEnum.CORRECT : QuestionAnswerEnum.INCORRECT,
                    percentage: 30,
                },
            ];
            break;
        case ELEMENT.PHOTO_ANSWER_QUESTION:
            reportAnswers = question.answers?.map((a: IElementQuestionAnswer, i: number, answers: IElementQuestionAnswer[]) => ({
                answerText: a.text || String(i),
                noOfAnswers: (i + 1) * 100,
                type: a.type,
                percentage: getPercentage(answers.length, i),
            }));
            break;
        case ELEMENT.SEQUENCE_QUESTION:
            reportAnswers = [
                ...question.sequence.map((a: IElementQuestionAnswer, i: number, answers: IElementQuestionAnswer[]) => ({
                    answerText: a.text,
                    noOfAnswers: (i + 1) * 100,
                    type: undefined,
                    percentage: getPercentage(answers.length, i),
                })),
            ];
            break;
        case ELEMENT.IMAGE_MARKUP:
            reportAnswers = [
                ...question.markedAreas.map((_: IShape, i: number, areas) => ({
                    answerText: `${i + 1}`,
                    noOfAnswers: (i + 1) * 100,
                    type: QuestionAnswerEnum.CORRECT as const,
                    percentage: getPercentage(areas.length, i),
                })),
            ];
            break;
        case ELEMENT.MATCH_PAIR_QUESTION:
            reportAnswers = [
                ...question.pairs.map((_: {leftElementId: string; rightElementId: string}, i: number, pairs) => ({
                    answerText: getMatchPairAnswer(question, i),
                    noOfAnswers: (i + 1) * 100,
                    type: QuestionAnswerEnum.CORRECT as const,
                    percentage: getPercentage(pairs.length, i),
                })),
            ];
            break;
        default:
            reportAnswers =
                'answers' in question
                    ? question.answers?.map((a: IElementQuestionAnswer, i: number, answers: IElementQuestionAnswer[]) => ({
                          answerText: a.text || String(i),
                          noOfAnswers: (i + 1) * 100,
                          type: a.type,
                          percentage: getPercentage(answers.length, i),
                      })) || []
                    : [];
    }
    return addAllCorrectAnswerIfNeeded(reportAnswers, question);
};

const WORD_CLOUD_WIDTH = '50%';
const WORD_CLOUD_HEIGHT = '40%';

export const wordCloudReportLayout = (height?: string): {fitContent: false; width?: string; height?: string} => {
    const collapsed = parseFloat(height || '0') < 8;
    return {
        fitContent: false,
        ...(collapsed ? {width: WORD_CLOUD_WIDTH, height: WORD_CLOUD_HEIGHT} : {}),
    };
};

export const isWordCloudSource = (element?: IElement): boolean => element?.type === ELEMENT.TEXT_INPUT;

export const getReportSourceLabel = (element: IElement): string => {
    switch (element.type) {
        case ELEMENT.TEXT_INPUT:
            return element.label;
        default:
            return 'question' in element && element.question ? element.question : element.name;
    }
};

export const getQuestions = (presentation: IPresentation): IElement[] =>
    presentation.slides.reduce((questions: IElement[], slide) => {
        slide.elements.forEach((element) => {
            if (interactiveElementTypes.includes(element.type)) {
                questions.push(element);
            }
        });
        return questions;
    }, []);
